import React, { useMemo, useState } from 'react';
import { useAllocationSearch } from './AllocationSearchContext';
import { ModalContent, ModalFooter } from '../../modal';
import { AddInvestmentCommonHeader } from '../../modals/add-investment-common-modal';
import { extractLeafStrategies } from '../../modals/data-uploader/utils/strategyUtils';
import { Command, CommandInput, CommandItem, CommandList, CommandEmpty } from '@/components/ui/command';
import { createFixedStrategiesKey, useHasFF } from 'venn-utils';
import { useQuery } from '@tanstack/react-query';
import { getHoldingsCategories, type HoldingsBenchmarkCategory } from 'venn-api';
import { cn } from '@/lib/utils';
import { Button, Icon } from 'venn-ui-kit';

interface AllocationSearchStrategyViewRedesignedProps {
  onClose: () => void;
  hasTaxInformation: boolean;
}

export const AllocationSearchStrategyViewRedesigned: React.FC<AllocationSearchStrategyViewRedesignedProps> = ({
  onClose,
  hasTaxInformation,
}) => {
  const { selectedFundForStrategyAssignment, handleStrategySelected, handleBack, handleAddTaxInformation } =
    useAllocationSearch();
  const [searchQuery, setSearchQuery] = useState('');
  const hasManualStrategyAssignment = useHasFF('rcm_strategy_enforcement_ff');

  const { data: categories = [] } = useQuery<HoldingsBenchmarkCategory[]>(
    createFixedStrategiesKey('ASSET', 'WORKSPACE'),
    async () => (await getHoldingsCategories('ASSET', 'WORKSPACE')).content || [],
    { staleTime: 10 * 60 * 1000, keepPreviousData: true, enabled: hasManualStrategyAssignment },
  );

  const availableStrategies = useMemo(() => extractLeafStrategies(categories), [categories]);

  const filteredStrategies = useMemo(() => {
    if (!searchQuery) return availableStrategies;

    const query = searchQuery.toLowerCase();
    return availableStrategies.filter((strategy) => strategy.categoryName?.toLowerCase().includes(query));
  }, [availableStrategies, searchQuery]);

  if (!selectedFundForStrategyAssignment || !hasManualStrategyAssignment) {
    return null;
  }

  const handleStrategySelect = (strategy: HoldingsBenchmarkCategory) => {
    if (strategy.id && strategy.categoryName) {
      handleStrategySelected(strategy.id.id, strategy.categoryName);
      setSearchQuery('');
    }
    if (hasTaxInformation) {
      handleAddTaxInformation();
    } else {
      onClose();
    }
  };

  const title = (
    <>
      <strong>Assign level 4 strategy to: </strong>
      {selectedFundForStrategyAssignment.name}
    </>
  );

  const searchIcon = <Icon type="search" className="text-[14px]" />;

  return (
    <>
      <AddInvestmentCommonHeader title={title} onClose={onClose} />
      <ModalContent className="mt-4 p-0 flex-1 min-h-0 overflow-hidden">
        <Command shouldFilter={false} className="">
          <div className="relative mb-4 px-4">
            <CommandInput
              placeholder="Search for a strategy…"
              value={searchQuery}
              onValueChange={setSearchQuery}
              wrapperClassName="border border-solid border-venn-grey-50 min-h-[38px] focus-within:border-venn-dark-blue"
              customIcon={searchIcon}
              className="placeholder:text-venn-grey-60"
            />
          </div>

          <CommandList className="flex-1 overflow-y-auto overflow-x-visible max-h-full [&>div>div:first-child]:border-t">
            <CommandEmpty className="p-4 text-center text-gray-500">No strategies found.</CommandEmpty>

            {filteredStrategies.map((strategy) => (
              <CommandItem
                key={strategy.id?.id}
                value={strategy.categoryName || ''}
                onSelect={() => handleStrategySelect(strategy)}
                className={cn(
                  'py-2 cursor-pointer hover:bg-venn-grey-10 border-0 border-solid border-b border-venn-grey-30 last:border-b-0',
                  'flex items-center justify-start text-left text-[14px] px-4',
                )}
              >
                {strategy.categoryName}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </ModalContent>
      <ModalFooter
        className="!bg-venn-white px-4 py-3 border-0 border-t border-solid border-venn-grey-30 h-16"
        leftChildren={
          <Button refreshedStyling noMargin icon="chevron-left" onClick={handleBack}>
            Back
          </Button>
        }
      />
    </>
  );
};
